import React, { DOMAttributes, useCallback } from 'react'
import styled, { css } from 'styled-components'
import { IconChevronBottomFilled } from '@chilipiper/icons/figma-generated/src/icon/ChevronBottomFilled'
import { ListState } from 'react-stately'
import invariant from 'tiny-invariant'
import { Icon } from '../../../../old/icon/Icon'
import { OverflowPillWithMeasurer } from '../../input/pill-combobox/OverflowPill'
import { SettingLabel } from '../../wayfinding/setting-label/SettingLabel'
import { UnstyledButton } from '../../../core-components/unstyled-button/UnstyledButton'
import type { AriaProps } from '../../types'
import { Flex, Grid, Text } from '../../../core-components'
import { Skeleton, SkeletonLine } from '../../../../old'
import { ItemData } from '../base-select/itemData.types'
import { DynamicItem } from '../base-select/dynamicItemUtils'
import { isCascadeSelectValue, isCascadeSelectValues } from '../base-select/cascadeSelectUtils'
import { useLabelPath } from '../base-select/DefaultSelectedText'
import { CorePill } from './CorePill'
import { CascadeItemsValuesProps, CascadeSelectValue } from './PillSelect'

type Item<Value extends string | CascadeSelectValue> = ItemData & {
  value: Value
  isVisible: boolean
}
interface Props<Value extends string | CascadeSelectValue> {
  invisibleValuesCount?: number
  isDisabled?: boolean
  isWrapping: boolean
  onChange?: (value: Array<Value>) => void
  pillsWrapperRef?: React.RefObject<HTMLDivElement | null>
  placeholder?: string
  values: ReadonlyArray<Value>
  items: Array<Item<Value>>
  listState?: ListState<DynamicItem>
  isLoading?: boolean
}

export const Pills = <Value extends string | CascadeSelectValue>({
  pillsWrapperRef,
  values,
  invisibleValuesCount = 0,
  isDisabled,
  onChange,
  placeholder,
  isWrapping,
  items,
  listState,
  isLoading,
}: Props<Value>) => {
  return (
    <Flex
      alignItems='center'
      gap={2}
      flexGrow={1}
      flexWrap={isWrapping ? 'wrap' : undefined}
      minWidth={0 /* to make textOverflow: ellipsis work */}
      ref={pillsWrapperRef}
    >
      {values.length === 0 && placeholder ? (
        <Text textStyle='form-value' color='fg/minor' py='7px' pl={3}>
          {placeholder}
        </Text>
      ) : null}
      {items.map(item => {
        const {
          startContent,
          children,
          dataTestId,
          dataId,
          isDisabled: isPillDisabled,
          textValue,
        } = item

        return (
          <Pill<Value>
            key={item.textValue}
            isDisabled={isDisabled || isPillDisabled}
            onChange={onChange}
            values={values}
            value={item.value}
            startContent={startContent}
            data-test-id={dataTestId}
            data-id={dataId}
            textValue={textValue}
            isVisible={item.isVisible}
            isTheOnlyVisible={Boolean(items.at(0)?.isVisible && !items.at(1)?.isVisible)}
            isWrapping={isWrapping}
            listState={listState}
          >
            {!children && isLoading ? (
              <Skeleton height={14} width={100}>
                <SkeletonLine y={3} width={100} />
              </Skeleton>
            ) : (
              children
            )}
          </Pill>
        )
      })}
      <OverflowPillWithMeasurer
        invisibleValuesCount={invisibleValuesCount}
        totalValuesCount={values.length}
      />
    </Flex>
  )
}

const Pill = <Value extends string | CascadeSelectValue>({
  onChange,
  values,
  value,
  listState,
  ...corePillProps
}: React.ComponentPropsWithoutRef<typeof CorePill> & {
  onChange?: (value: Array<Value>) => void
  values: ReadonlyArray<Value>
  value: Value
  listState?: ListState<DynamicItem>
}) => {
  const handleRemove = useCallback(() => {
    if (isCascadeSelectValues(values)) {
      invariant(isCascadeSelectValue(value))

      const newValues = (values as ReadonlyArray<CascadeSelectValue>).filter(
        valueItem =>
          valueItem.value !== value.value || valueItem.path.join('.') !== value.path.join('.')
      ) as Value[]

      return onChange?.(newValues)
    }

    onChange?.(values.filter(oldValue => oldValue !== value))
  }, [onChange, values, value])

  if (isCascadeSelectValue(value)) {
    invariant(listState, 'listState should be defined')

    return (
      <CascadePill
        {...corePillProps}
        onRemove={onChange ? handleRemove : undefined}
        value={value}
        listState={listState}
      />
    )
  }

  return <CorePill {...corePillProps} onRemove={onChange ? handleRemove : undefined} />
}
Pill.displayName = 'Pill'

export const SelectContainer = ({
  children,
  'data-test-id': dataTestId,
  variant,
  isDisabled,
  ariaProps,
  isOpen,
  h,
  triggerRef,
  rootProps,
}: {
  ariaProps?: AriaProps
  children: React.ReactNode
  'data-test-id'?: string
  h?: number
  isDisabled?: boolean
  isOpen: boolean
  rootProps?: DOMAttributes<unknown>
  triggerRef?: React.RefObject<HTMLDivElement | null>
  variant: 'regular' | 'nested'
}) => {
  return (
    <StyledGrid isOpen={isOpen} h={h} position='relative' {...rootProps} ref={triggerRef}>
      <StyledButton
        gridArea='1/-1'
        data-test-id={dataTestId}
        disabled={isDisabled}
        gap={2}
        bg={variant === 'regular' ? 'bg/form-item-default' : 'transparent'}
        boxShadow='border/section'
        data-variant={variant}
        ariaProps={ariaProps}
      />
      <StyledDisableEventsFlex
        zIndex={1}
        gridArea='1/-1'
        pl={2}
        pr={5}
        py={variant === 'nested' ? '10px' : '6px'}
        justifyContent='space-between'
        minWidth={0 /* to make textOverflow: ellipsis work */}
      >
        {children}
        <StyledIcon
          ml={6}
          mt='6px'
          isOpen={isOpen}
          flexShrink={0}
          icon={IconChevronBottomFilled}
          color='fg/minor'
        />
      </StyledDisableEventsFlex>
    </StyledGrid>
  )
}
const StyledGrid = styled(Grid)<{ isOpen: boolean }>`
  overflow: hidden;

  ${({ isOpen }) =>
    isOpen &&
    css`
      overflow: visible;
      height: auto;
    `}
  :focus-within,
  :hover {
    overflow: visible;
    height: auto;
  }
`
const StyledIcon = styled(Icon)<{ isOpen: boolean }>`
  transform: ${({ isOpen }) => (isOpen ? 'rotate(180deg)' : undefined)};
`
const StyledDisableEventsFlex = styled(Flex)`
  pointer-events: none;
`
const StyledButton = styled(UnstyledButton)`
  border-radius: ${({ theme }) => theme.radii[10]};

  &[data-variant='nested'] {
    border-radius: ${({ theme }) => theme.radii['none']};

    ::after {
      box-shadow: none;
    }
  }

  :hover {
    background: ${({ theme }) => theme.colors['bg/form-item-hovered']};

    ::after {
      box-shadow: none;
    }
  }

  :focus-visible {
    background: ${({ theme }) => theme.colors['bg/form-item-hovered']};

    ::after {
      box-shadow: ${({ theme }) => theme.shadows['border/focus-indicator']};
    }
  }

  &[aria-expanded='true'] {
    background: ${({ theme }) => theme.colors['bg/form-item-focused']};

    ::after {
      box-shadow: ${({ theme }) => theme.shadows['border/form-item-focused']};
    }
  }

  :disabled {
    background: ${({ theme }) => theme.colors['bg/disabled']};

    ::after {
      box-shadow: none;
    }
  }
`
export const Label = (props: React.ComponentProps<typeof SettingLabel>) => (
  <SettingLabel {...props} />
)

const CascadePill = ({
  value,
  listState,
  ...corePillProps
}: React.ComponentPropsWithoutRef<typeof CorePill> & {
  value: CascadeItemsValuesProps['values'][0]
  listState: ListState<DynamicItem>
}) => {
  const rootValue = listState.collection.getItem(value.path[0])

  invariant(rootValue, 'rootValue is not defined')

  const { label, hiddenListsToRender } = useLabelPath(rootValue, [...value.path, value.value])

  const lastLabelItem = label.at(-1)

  invariant(lastLabelItem, 'lastLabelItem is not defined')

  return (
    <CorePill
      {...corePillProps}
      {...lastLabelItem}
      labelPrefix={
        label.length > 1
          ? label
              .slice(0, -1)
              .map(item => ({ label: item.label as string, startContent: item.startContent }))
          : undefined
      }
    >
      {lastLabelItem.label}
      <div aria-hidden style={{ display: 'none' }}>
        {hiddenListsToRender}
      </div>
    </CorePill>
  )
}
